import { useAuthorization } from '@frontend/authorization';
import { useModals } from '@frontend/modal';
import { useListRepository } from '@frontend/repository';
import { ColumnDef } from '@tanstack/react-table';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { ObjectColumns } from '../object-column-mapper';
import { ObjectListProps } from './object-list.component';

interface ViewProps<T extends object> extends ReturnType<typeof useListRepository<T>> {
    data: T[] | null;
    error: boolean;
    columns: ColumnDef<T>[];
    changeSelectedObjects: Dispatch<SetStateAction<T[]>>;
    selectedObjects: T[];
    mergeMaps: <K, V>(map1: Map<K, V>, map2?: Map<K, V>) => Map<K, V>;
    optionsModalOpen: boolean;
    setOptionsModalOpen: Dispatch<SetStateAction<boolean>>;
    openModal: ReturnType<typeof useModals>['openModal'];
    closeModal: ReturnType<typeof useModals>['closeModal'];
    multiDeleteModalId: string;
}

const useObjectList = <T extends { id: string }>(props: ObjectListProps<T>): ViewProps<T> => {
    const [searchParams] = useSearchParams();
    const initialActiveFilters = useMemo(
        () =>
            getInitialActiveFiltersFromSearchParams(
                searchParams,
                props.activeFilter?.searchKeys,
                props.activeFilter?.filterKeys,
                props.activeFilter?.queryKeyMap
            ),
        [searchParams, props.activeFilter?.searchKeys, props.activeFilter?.filterKeys, props.activeFilter?.queryKeyMap]
    );
    const defaultListValues = useMemo(() => {
        if (!initialActiveFilters || Object.keys(initialActiveFilters).length === 0) return props.defaultListValues;
        return {
            ...props.defaultListValues,
            activeFilters: {
                ...(props.defaultListValues?.activeFilters ?? {}),
                ...initialActiveFilters
            }
        };
    }, [props.defaultListValues, initialActiveFilters]);

    const repository = useListRepository<T>({
        fetch: (arg) => props.fetch(arg),
        dispatch: props.dispatch,
        eventListener: props.eventListener,
        defaultListValues,
        listId: props.useStaticListId === true ? props.id : undefined
    });
    const [selectedObjects, changeSelectedObjects] = useState<T[]>([]);
    const { permissions } = useAuthorization();
    const [optionsModalOpen, setOptionsModalOpen] = useState<boolean>(false);
    const { openModal, closeModal } = useModals();
    const multiDeleteModalId = 'multi-delete-modal';

    useEffect(() => {
        props.listStoreIdCallback && props.listStoreIdCallback(repository.listId);
    }, [repository.listId, props.listStoreIdCallback]);

    const columns = useMemo(
        () =>
            ObjectColumns<T>({
                ...props.columns,
                dispatch: props.dispatch,
                selectEnabled: !!props.multiSelectOptions,
                listStoreId: repository.listId,
                permissions: permissions,
                openModal,
                closeModal
            }),
        [props.locale, permissions]
    );

    useEffect(() => {
        changeSelectedObjects(repository.selected);
        props.selectedObjects && props.selectedObjects(repository.selected);
    }, [repository.selected]);

    function mergeMaps<K, V>(map1: Map<K, V>, map2?: Map<K, V>): Map<K, V> {
        if (!map2) return map1;
        const mergedMap = new Map(map1);
        map2.forEach((value, key) => {
            mergedMap.set(key, { ...map1.get(key), ...value });
        });
        return mergedMap;
    }

    return {
        ...repository,
        data: repository.data,
        error: repository.error,
        columns: columns,
        selectedObjects,
        changeSelectedObjects,
        mergeMaps,
        optionsModalOpen,
        setOptionsModalOpen,
        closeModal,
        openModal,
        multiDeleteModalId
    };
};

export default useObjectList;

const getInitialActiveFiltersFromSearchParams = (
    searchParams: URLSearchParams,
    searchKeys?: string[],
    filterKeys?: string[],
    queryKeyMap?: Map<string, string>
): Record<string, string[]> | undefined => {
    const filterQueryKeys = filterKeys?.map((key) => queryKeyMap?.get(key) || key) ?? [];
    const keys = filterKeys?.includes('waiting') ? ['before', 'after', ...(searchKeys ?? []), ...filterQueryKeys] : [...(searchKeys ?? []), ...filterQueryKeys];
    const activeFilters = keys.reduce<Record<string, string[]>>((filters, key) => {
        const values = searchParams.getAll(key);
        if (values.length > 0) {
            filters[key] = values;
        }
        return filters;
    }, {});

    return Object.keys(activeFilters).length > 0 ? activeFilters : undefined;
};
